import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { tokenStorage } from "../api/tokenStorage";
import { AuthUserProps } from "../types/auth";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  /** Access token lifetime in seconds (from API response `expires_in`) */
  expiresIn: number;
  /** Refresh token lifetime in seconds (from API response `refresh_token_expires_in`) */
  refreshTokenExpiresIn: number;
}

interface AuthStore {
  /** Current user profile — null until /auth/me resolves */
  user: AuthUserProps | null;

  /**
   * Access token kept in Zustand so routing guards (ProtectedLayout,
   * PublicLayout, DefaultLayout) can react to it as normal React state.
   * The source of truth for the raw token value is the cookie; this field
   * is initialised from the cookie at module load and updated on every
   * login / refresh / logout action.
   */
  authToken: string | null;
  refreshToken: string | null;

  // ── Actions ────────────────────────────────────────────────────────────────
  /** Called after a successful login. Writes tokens to cookies with API-supplied expiry. */
  loginSuccess: (tokens: AuthTokens) => void;

  /** Called by the axios interceptor (via window event) when it silently
   *  refreshes the access token so Zustand stays in sync. */
  updateAccessToken: (
    accessToken: string,
    refreshToken?: string,
    expiresIn?: number,
    refreshTokenExpiresIn?: number,
  ) => void;

  /** Stores the user profile returned by /auth/me */
  setUser: (user: AuthUserProps | null) => void;

  /** Clears all auth state and removes token cookies */
  logout: () => void;
}

// ─── Bootstrap — read existing tokens from cookies at module load ─────────────
//
// Cookies survive page reloads natively (until max-age elapses), so we just
// read them once here instead of mirroring them through localStorage.

const cookieAccessToken = tokenStorage.getAccessToken();
const cookieRefreshToken = tokenStorage.getRefreshToken();

// ─── Store ────────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      authToken: cookieAccessToken,
      refreshToken: cookieRefreshToken,

      loginSuccess: ({
        accessToken,
        refreshToken,
        expiresIn,
        refreshTokenExpiresIn,
      }) => {
        tokenStorage.setTokens(
          accessToken,
          refreshToken,
          expiresIn,
          refreshTokenExpiresIn,
        );
        set({ authToken: accessToken, refreshToken, user: null });
      },

      updateAccessToken: (
        accessToken,
        refreshToken,
        expiresIn = 7200,
        refreshTokenExpiresIn,
      ) => {
        tokenStorage.setAccessToken(accessToken, expiresIn);
        if (refreshToken && refreshTokenExpiresIn) {
          tokenStorage.setRefreshToken(refreshToken, refreshTokenExpiresIn);
        }
        set((state) => ({
          authToken: accessToken,
          refreshToken: refreshToken ?? state.refreshToken,
        }));
      },

      setUser: (user) => set({ user }),

      logout: () => {
        tokenStorage.clearTokens();
        set({ user: null, authToken: null, refreshToken: null });
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      // Tokens live in cookies — only persist the user profile in localStorage
      // so the UI can render the user's name immediately on page reload
      // without waiting for /auth/me to resolve.
      partialize: (state) => ({ user: state.user }),
    },
  ),
);

// ─── Global window event listeners ───────────────────────────────────────────
//
// The axios interceptor dispatches these custom events instead of importing
// this store directly, which would create a circular dependency.

if (typeof window !== "undefined") {
  /** Interceptor successfully refreshed the access token — keep store in sync */
  window.addEventListener("auth:token-refreshed", (e: Event) => {
    const { accessToken, refreshToken, expiresIn, refreshTokenExpiresIn } = (
      e as CustomEvent<AuthTokens>
    ).detail;
    useAuthStore
      .getState()
      .updateAccessToken(
        accessToken,
        refreshToken,
        expiresIn,
        refreshTokenExpiresIn,
      );
  });

  /** Refresh token is expired / invalid — force logout */
  window.addEventListener("auth:unauthorized", () => {
    useAuthStore.getState().logout();
  });
}
